/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import type { Collection, Collections } from '../../../../ts/collection';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';
import { endpoints } from '../../../ia/util/config';

const listCollectionsEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  useNewIAUpdatedAccess
    ? `${endpoints.store}/v2/tooling/assistants/${assistantId}/skills/${skillId}/action_table`
    : `${endpoints.store}/v2/tooling/skills/${skillId}/action_table`;

const collectionsEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/collections`
    )
  );

const collectionEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  collection: string
): string =>
  `${collectionsEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  )}/${collection}`;

export const createCollection = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  options: any
): Promise<Collection> => {
  const url = collectionsEndpoint(skillId, useNewIAUpdatedAccess, assistantId);

  return await axios
    .post(url, options)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const updateCollection = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  collection: string,
  options: any
) => {
  const url = collectionEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    collection
  );

  return await axios
    .post(url, options)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const getCollection = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  collection: string
): Promise<Collection> => {
  const url = collectionEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    collection
  );

  return await axios
    .get(url)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const deleteCollection = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  collection: string,
  deleteActions: boolean
) => {
  const url = collectionEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    collection
  );
  const params = { delete_actions: false };

  if (deleteActions) {
    params.delete_actions = true;
  }
  return await axios
    .delete(url, { params })
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

export const listCollections = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): Promise<Collections> => {
  const url = listCollectionsEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const params = { include_audit: true };
  return await axios
    .get(url, { params })
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};
